// partners.js - What an affiliate is given and what they are paid, as the
// public pages quote it.
//
// Four numbers, all set on the settings screen and all stated in public: the
// commission, the most it pays on one order, how many mailers come free with the
// display, and what a restock costs. Two pages quote them, Work With Us and the
// application form, so they are read from one place here rather than each page
// keeping its own copy of the fallbacks and its own way of saying "no cap".
//
// The fallbacks are what the prerendered HTML carries, which is what a crawler
// and somebody with JavaScript switched off read. The prerenderer runs at build
// time with no database, so a page cannot know the live figure until it has
// hydrated and asked. They must therefore equal the defaults on the server's
// Setting model, and a smoke check fails if they drift apart.
//
// LEGEND
//   1. PARTNER_DEFAULTS  the figures a page shows before settings arrive
//   2. partnerTerms()    the four numbers, from publicSettings or the defaults
//   3. rateLine()        the commission said as one sentence, cap included

import { percent, price, money } from "./format.js";

////////// 1. BEFORE SETTINGS ARRIVE //////////

export const PARTNER_DEFAULTS = {
  commissionPercent: 5,
  commissionCap: 250,
  startingMailers: 20,
  mailerPrice: 2,
};

////////// 2. THE FOUR NUMBERS //////////

export function partnerTerms(settings) {
  const from = settings?.partners || {};
  const pick = (key) => {
    const value = Number(from[key]);
    return Number.isFinite(value) && from[key] !== null && from[key] !== "" ? value : PARTNER_DEFAULTS[key];
  };

  return {
    commissionPercent: pick("commissionPercent"),
    commissionCap: pick("commissionCap"),
    startingMailers: pick("startingMailers"),
    mailerPrice: pick("mailerPrice"),
  };
}

////////// 3. THE RATE, IN WORDS //////////
// A cap of zero means no cap, the same as it does in `commissionFor()` on the
// server. Printing "up to $0 on any one order" would describe a programme that
// pays nothing, which is the opposite of the setting.

export function rateLine(terms, { perOrder = "on any one order" } = {}) {
  const rate = `${percent(terms.commissionPercent)} of what each customer you send is paid`;
  return terms.commissionCap > 0
    ? `${rate}, up to ${money(terms.commissionCap)} ${perOrder}`
    : `${rate}, with no cap`;
}

// Re-exported so a page quoting these numbers has one import for all of it.
export { price, percent, money };
